import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Play, BarChart3, Zap, Briefcase, Award, Trophy, Settings, User,
  MessageSquare, Users, GraduationCap, Plug, FileText, Phone, Target,
  Mic, LayoutDashboard, Calendar, Plus, Building2, UserPlus, Loader2,
  History, Bot,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardSearch, type SearchResultType } from '@/hooks/useDashboardSearch';
import { COMMAND_PALETTE_OPEN_EVENT } from '@/lib/commandPalette';

// Keep these values in sync with DashboardSidebar's navItems.
const navigationItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Practice', icon: Play, path: '/dashboard?tab=practice' },
  { label: 'Create Scenario', icon: Plus, path: '/dashboard?tab=create' },
  // Temporarily hidden: Competitions & Academy
  // { label: 'Competitions', icon: Zap, path: '/dashboard?tab=competitions' },
  // { label: 'Academy', icon: GraduationCap, path: '/dashboard?tab=tutorials' },
  { label: 'Progress', icon: BarChart3, path: '/dashboard?tab=progress' },
  { label: 'Practice History', icon: Calendar, path: '/dashboard?tab=recent-sessions' },
  { label: 'Achievements', icon: Award, path: '/dashboard?tab=achievements' },
  { label: 'Leaderboards', icon: Trophy, path: '/dashboard?tab=leaderboards' },
  { label: 'Career', icon: Briefcase, path: '/dashboard?tab=jobs' },
  { label: 'Groups', icon: Building2, path: '/dashboard?tab=organizations' },
  { label: 'Instructor Dashboard', icon: Users, path: '/dashboard?tab=students' },
  { label: 'Recruiter', icon: UserPlus, path: '/dashboard?tab=recruiter' },
  { label: 'Sessions History', icon: FileText, path: '/sessions' },
  { label: 'Profile', icon: User, path: '/profile' },
  { label: 'Settings', icon: Settings, path: '/settings' },
  { label: 'Messages', icon: MessageSquare, path: '/messages' },
  { label: 'Connections', icon: Users, path: '/connections' },
  { label: 'Integrations', icon: Plug, path: '/integrations' },
  { label: 'Teams', icon: GraduationCap, path: '/teams' },
];

const actionItems = [
  { label: 'Speed Sell', icon: Mic, path: '/speed-sell' },
  { label: 'Cold Call Practice', icon: Phone, path: '/cold-call' },
  { label: 'AI Calling', icon: Target, path: '/ai-calling' },
  { label: 'Grade a Recording', icon: FileText, path: '/grade-recording' },
  { label: 'Resume Match', icon: FileText, path: '/resume-match' },
  { label: 'Job Board', icon: Briefcase, path: '/jobs' },
];

const RESULT_META: Record<SearchResultType, { heading: string; icon: typeof Play }> = {
  session: { heading: 'Practice History', icon: History },
  scenario: { heading: 'Scenarios', icon: Bot },
  competition: { heading: 'Competitions', icon: Zap },
  job: { heading: 'Jobs', icon: Briefcase },
  course: { heading: 'Academy', icon: GraduationCap },
};

const RESULT_ORDER: SearchResultType[] = ['session', 'scenario', 'competition', 'job', 'course'];

const matches = (label: string, query: string) =>
  label.toLowerCase().includes(query.toLowerCase().trim());

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { results, isSearching, isActive } = useDashboardSearch(open ? query : '');

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', down);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpen);
    return () => {
      document.removeEventListener('keydown', down);
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpen);
    };
  }, []);

  // Reset the query each time the palette opens so it never reopens mid-search.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const runCommand = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  if (!user) return null;

  const trimmed = query.trim();
  const filteredNav = trimmed ? navigationItems.filter(i => matches(i.label, trimmed)) : navigationItems;
  const filteredActions = trimmed ? actionItems.filter(i => matches(i.label, trimmed)) : actionItems;
  const hasAnything = filteredNav.length > 0 || filteredActions.length > 0 || results.length > 0;

  return (
    /* Filtering is done manually so server-side results are never dropped by cmdk. */
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search sessions, scenarios, competitions, jobs..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {!hasAnything && !isSearching && (
          <div className="py-6 text-center text-sm text-muted-foreground">No results found.</div>
        )}

        {RESULT_ORDER.map(type => {
          const group = results.filter(r => r.type === type);
          if (group.length === 0) return null;
          const { heading, icon: Icon } = RESULT_META[type];
          return (
            <CommandGroup key={type} heading={heading}>
              {group.map(result => (
                <CommandItem
                  key={`${type}-${result.id}`}
                  value={`${type}-${result.id}`}
                  onSelect={() => runCommand(result.path)}
                >
                  <Icon className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate">{result.title}</span>
                  {result.subtitle && (
                    <span className="ml-2 truncate text-xs text-muted-foreground">
                      {result.subtitle}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}

        {isSearching && results.length === 0 && (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching your dashboard...
          </div>
        )}

        {isActive && results.length > 0 && <CommandSeparator />}

        {filteredNav.length > 0 && (
          <CommandGroup heading="Navigate">
            {filteredNav.map(item => (
              <CommandItem key={item.path} value={item.path} onSelect={() => runCommand(item.path)}>
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {filteredNav.length > 0 && filteredActions.length > 0 && <CommandSeparator />}

        {filteredActions.length > 0 && (
          <CommandGroup heading="Quick Actions">
            {filteredActions.map(item => (
              <CommandItem key={item.path} value={item.path} onSelect={() => runCommand(item.path)}>
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
